import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MainLayout } from "@/layouts/MainLayout";
import { AnimatedSection, AnimatedText, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { 
  ArrowRight, 
  CheckCircle, 
  Shield, 
  Target, 
  Clock, 
  Eye,
  Compass,
  Users,
  TrendingUp,
  FileText,
  Heart
} from "lucide-react";
import ServicesOrbit from "@/components/ServicesOrbit";
import DifferentialsAccordion from "@/components/DifferentialsAccordion";
import OfferingsCarousel from "@/components/OfferingsCarousel";
import ProcessTimeline from "@/components/ProcessTimeline";
import ServicesSection from "@/components/ServicesSection";

// Wealth imagery imports
import bankExecutivesImage from "@/assets/images/bank-executives-conversation.jpg";
import executivesMeetingImage from "@/assets/images/executives-meeting.jpg";
import nycSkylineNightImage from "@/assets/images/nyc-skyline-night.jpg";
import brazilianProfessionalImage from "@/assets/images/brazilian-professional-woman.jpg";
import professionalPlanningImage from "@/assets/images/professional-planning.jpg";

const Index = () => {
  return (
    <MainLayout>
      {/* HERO */}
      <section className="relative min-h-[100dvh] flex items-center overflow-hidden pt-20 pb-8 md:pt-0 md:pb-0">
        {/* Background base */}
        <div className="absolute inset-0 bg-background z-0" />
        
        {/* Image on the left with opacity gradient */}
        <div className="absolute inset-y-0 left-0 w-full lg:w-2/3 z-0">
          <img 
            src={bankExecutivesImage} 
            alt="Executivos em ambiente bancário de luxo"
            className="w-full h-full object-cover object-[50%_20%] sm:object-[50%_30%] lg:object-center opacity-40"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/30 via-background/60 to-background" />
        </div>
        
        <div className="container-wide relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Orbital - left side on desktop */}
            <div className="hidden lg:flex justify-center">
              <ServicesOrbit />
            </div>
            
            {/* Text content - right side */}
            <div className="text-center lg:text-left lg:pl-8">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="mb-4 md:mb-6"
              >
                <span className="label-sm">Consultoria Patrimonial Independente</span>
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-medium tracking-tight text-foreground leading-tight mb-4 md:mb-6"
              >
                Você ganha bem. Mas isso não garante que você vai{" "}
                <span className="text-primary">se aposentar cedo.</span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="text-base sm:text-lg md:text-xl text-foreground/80 mb-6 md:mb-8"
              >
                A Allu Capital ajuda profissionais de alta renda a transformar bons ganhos em <strong className="text-foreground font-semibold">um plano financeiro sólido</strong> — capaz de <strong className="text-foreground font-semibold">antecipar sua liberdade financeira</strong> e proteger o futuro da sua família.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex justify-center lg:justify-start"
              >
                <Link to="/contato" className="btn-primary rounded-full">
                  Descubra se você está no caminho certo
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCO — POSICIONAMENTO (Floating card on white background) */}
      <section className="bg-[#FAFAFA] py-6 sm:py-8 md:py-10">
        <div className="container-wide">
          {/* Floating image card */}
          <div className="relative overflow-hidden rounded-3xl shadow-2xl min-h-[400px] sm:min-h-[450px]">
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
              <img 
                src={nycSkylineNightImage} 
                alt="Skyline de Nova York à noite"
                className="w-full h-full object-cover object-center"
                width={1920}
                height={1080}
                loading="lazy"
                decoding="async"
              />
              {/* Dark overlay for better text readability */}
              <div className="absolute inset-0 bg-[#112711]/70" />
            </div>
            
            {/* Content */}
            <div className="relative z-10 py-16 sm:py-20 md:py-24 px-6 sm:px-10 md:px-16">
              <AnimatedSection className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-medium text-[#FAFAFA] leading-tight mb-4 sm:mb-6 text-glow">
                O problema não é quanto você ganha.<br />
                <span className="text-primary text-glow-gold">É não saber se o seu plano funciona.</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-[#FAFAFA] text-glow-subtle">
                Profissionais altamente qualificados costumam enfrentar o mesmo dilema: <strong className="text-white font-semibold">a renda cresce, mas a sensação de segurança não acompanha.</strong> Sem um planejamento financeiro estruturado, decisões importantes acabam sendo tomadas no impulso, no "achismo" ou com base em recomendações isoladas. O resultado é <strong className="text-white font-semibold">um plano frágil — e um futuro incerto.</strong>
              </p>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCO — COMO FUNCIONA */}
      <ProcessTimeline />

      {/* BLOCO — SERVIÇOS OFERTADOS */}
      <ServicesSection />

      {/* BLOCO — NÚMEROS / AUTORIDADE */}
      <section id="nossa-trajetoria" className="py-16 sm:py-20 md:py-24 bg-[#FAFAFA]">
        <div className="container-wide">
          {/* Centered Title */}
          <AnimatedSection className="text-center mb-10 sm:mb-12">
            <span className="text-sm text-[#112711]/60 mb-3 block">Nossa Trajetória</span>
            <div className="w-12 h-1 bg-[#112711] mb-6 mx-auto" />
            <h2 className="text-xl sm:text-2xl md:text-3xl font-serif text-[#112711] leading-tight max-w-2xl mx-auto" style={{ textShadow: '0 0 20px rgba(17, 39, 17, 0.3)' }}>
              Uma trajetória construída com{" "}
              <span className="text-2xl sm:text-3xl md:text-4xl font-bold">confiança e resultados.</span>
            </h2>
            <p className="text-sm sm:text-base text-[#112711]/70 mt-4 max-w-xl mx-auto">
              Desde 2016, construímos uma trajetória sólida de confiança e resultados em nosso ecossistema.
            </p>
          </AnimatedSection>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { number: "+40.000", label: "pessoas atendidas", icon: <Users className="w-8 h-8" /> },
              { number: "+6.000", label: "investidores", icon: <TrendingUp className="w-8 h-8" /> },
              { number: "NPS 95", label: "de satisfação", icon: <Heart className="w-8 h-8" /> },
            ].map((item, index) => (
              <AnimatedSection key={index} delay={index * 0.15}>
                <div 
                  className="flex flex-col items-center text-center p-6 sm:p-8 rounded-2xl bg-[#112711]/90 border border-[#112711] hover:bg-[#112711] hover:scale-[1.02] transition-all duration-500"
                  style={{ boxShadow: '0 0 20px rgba(17, 39, 17, 0.3), 0 4px 12px rgba(17, 39, 17, 0.2)' }}
                >
                  <div className="text-white mb-4">{item.icon}</div>
                  <span 
                    className="text-4xl sm:text-5xl font-serif font-medium text-[#F3DE2C] mb-2"
                    style={{ textShadow: '0 0 20px rgba(243, 222, 44, 0.5), 0 0 40px rgba(243, 222, 44, 0.3)' }}
                  >
                    {item.number}
                  </span>
                  <span className="text-sm sm:text-base text-white/90">{item.label}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCO — O QUE FAZEMOS (Floating card on white background) */}
      <section className="bg-[#FAFAFA] py-6 sm:py-8 md:py-10">
        <div className="container-wide">
          {/* Floating image card */}
          <div className="relative overflow-hidden rounded-3xl shadow-2xl min-h-[550px] sm:min-h-[500px]">
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
              <img
                src={brazilianProfessionalImage}
                alt="Profissional brasileira"
                className="w-full h-full object-cover object-top sm:object-right"
                width={1920}
                height={1080}
                loading="lazy"
                decoding="async"
              />
              {/* Dark gradient overlay from left */}
              <div className="absolute inset-0 bg-gradient-to-b sm:bg-gradient-to-r from-[#112711]/95 via-[#112711]/80 to-[#112711]/50 sm:to-transparent" />
            </div>

            {/* Content */}
            <div className="relative z-10 py-16 sm:py-20 md:py-24 px-6 sm:px-10 md:px-16 max-w-2xl">
              {/* Accent bar */}
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: 48 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="h-1 bg-primary mb-6"
              />

              {/* Title */}
              <AnimatedSection>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-medium text-[#FAFAFA] leading-tight mb-4">
                  Estratégia hoje. <span className="text-primary">Liberdade amanhã.</span>
                </h2>
                <p className="text-base sm:text-lg text-[#FAFAFA]/80 mb-6">
                  Um bom planejamento financeiro não é sobre cortar gastos ou fazer apostas arriscadas. É sobre <strong className="text-white font-semibold">organizar sua vida financeira</strong>, entender se seu plano faz sentido e ajustá-lo para <strong className="text-white font-semibold">alcançar seus objetivos no menor tempo possível</strong>, com segurança.
                </p>
                <p className="text-sm sm:text-base text-[#FAFAFA]/70 mb-6">
                  Em muitos casos, uma estruturação correta permite <strong className="text-white font-semibold">antecipar em anos a sua independência financeira</strong>, sem comprometer seu padrão de vida atual. <strong className="text-white font-semibold">Menos incerteza hoje. Mais liberdade no futuro</strong> — para você e sua família.
                </p>
              </AnimatedSection>

              {/* Bullet points */}
              <AnimatedSection delay={0.2}>
                <p className="text-[#FAFAFA]/80 text-sm sm:text-base mb-4">
                  Essa é a pergunta central que orienta todo o nosso trabalho.
                </p>
                <p className="text-[#FAFAFA]/90 text-base sm:text-lg mb-8">
                  Na Allu Capital, o planejamento financeiro é o ponto de partida. A partir dele, conectamos todas as decisões patrimoniais — investimentos, proteção, previdência e estruturação — a uma estratégia clara e coerente com seus objetivos de vida.
                </p>
              </AnimatedSection>

              {/* CTA */}
              <AnimatedSection delay={0.3}>
                <Link to="/contato" className="btn-primary rounded-full">
                  <ArrowRight className="mr-2 w-4 h-4" />
                  Inicie seu planejamento
                </Link>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCO — O QUE OFERECEMOS */}
      <OfferingsCarousel />

      {/* BLOCO — COMO FUNCIONA (moved to position 4) */}

      {/* BLOCO — PARA QUEM É (Floating Frame) */}
      <section className="bg-[#FAFAFA] py-6 sm:py-8 md:py-10">
        <div className="container-wide">
          {/* Floating image card */}
          <div className="relative overflow-hidden rounded-3xl shadow-2xl min-h-[650px] sm:min-h-[550px]">
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
              <img
                src={professionalPlanningImage}
                alt="Profissional fazendo planejamento estratégico"
                className="w-full h-full object-cover object-top sm:object-center"
                width={1920}
                height={1080}
                loading="lazy"
                decoding="async"
              />
              {/* Dark gradient overlay from left */}
              <div className="absolute inset-0 bg-gradient-to-b sm:bg-gradient-to-r from-[#112711]/95 via-[#112711]/85 to-[#112711]/60 sm:to-[#112711]/40" />
            </div>

            {/* Content */}
            <div className="relative z-10 py-16 sm:py-20 md:py-24 px-6 sm:px-10 md:px-16 max-w-3xl">
              {/* Accent bar */}
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: 48 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="h-1 bg-primary mb-6"
              />

              {/* Title */}
              <AnimatedSection>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-medium text-[#FAFAFA] leading-tight mb-8">
                  Para quem é a{" "}
                  <span className="text-primary" style={{ textShadow: '0 0 20px rgba(243, 222, 44, 0.5)' }}>
                    Allu Capital?
                  </span>
                </h2>
              </AnimatedSection>

              {/* Bullet points */}
              <AnimatedSection delay={0.2}>
                <ul className="space-y-4 mb-8">
                  {[
                    "Profissionais que querem um bom plano para se aposentarem mais cedo, sem queda no padrão de vida",
                    "Pessoas que ganham bem, mas não sabem se o plano atual realmente cumpre seus objetivos",
                    "Pessoas que buscam estruturação financeira e patrimonial 360, gerando segurança familiar e sucessória no longo prazo",
                    "Profissionais que querem clareza para tomar decisões financeiras relevantes com confiança",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-[#FAFAFA]/90 text-base sm:text-lg">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </AnimatedSection>

              {/* CTA */}
              <AnimatedSection delay={0.3}>
                <Link to="/contato" className="btn-primary rounded-full">
                  <ArrowRight className="mr-2 w-4 h-4" />
                  Fale com um especialista
                </Link>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCO — DIFERENCIAL ALLU CAPITAL */}
      <DifferentialsAccordion />

      {/* CTA FINAL */}
      <section className="relative py-16 sm:py-20 md:py-24 border-t border-border/30 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={executivesMeetingImage} 
            alt="Executivos em reunião"
            className="w-full h-full object-cover opacity-30"
            width={1920}
            height={1080}
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/60" />
        </div>
        
        <div className="container-wide relative z-10">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <h2 className="heading-md sm:heading-lg mb-4 sm:mb-6">
              Planejar bem hoje é{" "}
              <span className="text-primary">ganhar liberdade amanhã.</span>
            </h2>
            <p className="text-foreground/70 text-base sm:text-lg mb-6">
              Descubra se o seu plano financeiro está realmente preparado para sustentar o futuro que você deseja.
            </p>
            <Link to="/contato" className="btn-primary rounded-full">
              Agendar uma conversa
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </MainLayout>
  );
};

export default Index;
